/**
 * Movement integration shared by client and server.
 *
 * This is the single implementation mandated by AD-026: the client predicts
 * its own movement with exactly the code the server uses to validate and
 * integrate, which structurally prevents prediction drift
 * (docs/architecture/networking.md "Movement").
 *
 * Determinism rules:
 * - Pure function of (state, input, dtMs): no clocks, no randomness, no I/O.
 * - Dodge bookkeeping uses *remaining-time counters* decremented by dt, never
 *   absolute timestamps, so client replay and server integration agree even
 *   though their wall clocks differ.
 * - Both sides step with the same fixed dt (TICK_MS), one step per input.
 */
import { COLLISION_RELAXATION_PASSES, DODGE_COOLDOWN_MS, DODGE_DURATION_MS, DODGE_SPEED_MPS, MOVE_SPEED_MPS, PLAYER_COLLISION_RADIUS_M, } from './constants.js';
import { resolveCircle } from './collision.js';
import { clampToOutline } from './island.js';
export function createMovementState(x, z, facing = 0) {
    return {
        x,
        z,
        vx: 0,
        vz: 0,
        facing: wrapAngle(facing),
        dodgeRemainingMs: 0,
        dodgeCooldownMs: 0,
        dodgeDirX: 0,
        dodgeDirZ: 1,
    };
}
/** Wrap an angle to [-PI, PI]. Non-finite input collapses to 0. */
export function wrapAngle(a) {
    if (!Number.isFinite(a))
        return 0;
    let r = a % (2 * Math.PI);
    if (r > Math.PI)
        r -= 2 * Math.PI;
    if (r < -Math.PI)
        r += 2 * Math.PI;
    return r;
}
/** Clamp a raw move vector to the unit disc; non-finite components become 0. */
export function sanitiseMoveVector(moveX, moveZ) {
    let x = Number.isFinite(moveX) ? moveX : 0;
    let z = Number.isFinite(moveZ) ? moveZ : 0;
    if (x < -1)
        x = -1;
    if (x > 1)
        x = 1;
    if (z < -1)
        z = -1;
    if (z > 1)
        z = 1;
    const mag = Math.hypot(x, z);
    if (mag > 1) {
        x /= mag;
        z /= mag;
    }
    return { x, z };
}
/**
 * Advance movement by one fixed step.
 *
 * Step order is part of the contract (do not reorder — both sides must agree):
 * 1. sanitise input, 2. maybe start a dodge, 3. compute velocity,
 * 4. integrate and clamp to bounds, 4b. resolve static collision (WR.2;
 * skipped entirely when no collider is supplied, so the flat-plane behaviour
 * is byte-identical and AD-031 is preserved), 5. decrement dodge timers.
 */
export function stepMovement(state, input, dtMs, bounds, collider) {
    const move = sanitiseMoveVector(input.moveX, input.moveZ);
    const facing = wrapAngle(input.facing);
    let { dodgeRemainingMs, dodgeCooldownMs, dodgeDirX, dodgeDirZ } = state;
    // 2. Dodge start: only outside an active dodge and off cooldown.
    if (input.dodge && dodgeRemainingMs <= 0 && dodgeCooldownMs <= 0) {
        const mag = Math.hypot(move.x, move.z);
        if (mag > 0.01) {
            dodgeDirX = move.x / mag;
            dodgeDirZ = move.z / mag;
        }
        else {
            dodgeDirX = Math.sin(facing);
            dodgeDirZ = Math.cos(facing);
        }
        dodgeRemainingMs = DODGE_DURATION_MS;
        dodgeCooldownMs = DODGE_COOLDOWN_MS;
    }
    // 3. Velocity: dodge dash overrides normal movement (server-capped speeds).
    let vx;
    let vz;
    if (dodgeRemainingMs > 0) {
        vx = dodgeDirX * DODGE_SPEED_MPS;
        vz = dodgeDirZ * DODGE_SPEED_MPS;
    }
    else {
        vx = move.x * MOVE_SPEED_MPS;
        vz = move.z * MOVE_SPEED_MPS;
    }
    // 4. Integrate, clamp to zone bounds.
    const dtSec = dtMs / 1000;
    let x = state.x + vx * dtSec;
    let z = state.z + vz * dtSec;
    if (x < bounds.minX)
        x = bounds.minX;
    if (x > bounds.maxX)
        x = bounds.maxX;
    if (z < bounds.minZ)
        z = bounds.minZ;
    if (z > bounds.maxZ)
        z = bounds.maxZ;
    // AD-840: an island zone's walkable coastline (inside the rectangle) — the edge
    // slides like a wall. Absent ⇒ skipped, so a rectangle-only zone is unchanged.
    const outline = collider?.outline;
    if (outline !== undefined)
        ({ x, z } = clampToOutline(outline, x, z));
    // 4b. Resolve static collision against the zone's footprint shapes (WR.2).
    // Skipped when there is no collider or no shapes, so the flat-plane path is
    // byte-identical to Phase 3 (AD-031). The client predicts with this exact
    // code, so it can never disagree with the server about an obstacle (AD-026).
    if (collider !== undefined && collider.shapes.length > 0) {
        const resolved = resolveCircle(x, z, PLAYER_COLLISION_RADIUS_M, collider.shapes, COLLISION_RELAXATION_PASSES);
        x = resolved.x;
        z = resolved.z;
        // Bounds win over collision: an obstacle must never push a player out of
        // the zone (placement leaves a radius margin from the edge — WR.4).
        if (x < bounds.minX)
            x = bounds.minX;
        if (x > bounds.maxX)
            x = bounds.maxX;
        if (z < bounds.minZ)
            z = bounds.minZ;
        if (z > bounds.maxZ)
            z = bounds.maxZ;
        if (outline !== undefined)
            ({ x, z } = clampToOutline(outline, x, z));
    }
    // 5. Timers tick down after the step that used them.
    dodgeRemainingMs = Math.max(0, dodgeRemainingMs - dtMs);
    dodgeCooldownMs = Math.max(0, dodgeCooldownMs - dtMs);
    return { x, z, vx, vz, facing, dodgeRemainingMs, dodgeCooldownMs, dodgeDirX, dodgeDirZ };
}
/**
 * Derive the public animation state for an entity.
 *
 * `dodgeTellLive` (default true) lets the SERVER bound the dodge tell it puts on
 * the wire by its wall-tick evasion window (`q-dodge-tell-evasion` C-5, AD-555):
 * the sim only steps on a processed input, so a client that stops sending
 * mid-dodge freezes `dodgeRemainingMs > 0`, and without the bound every remote
 * held `anim: 'dodge'` indefinitely after the evasion frames had expired — a
 * tell with no evasion. With the tell withdrawn the state reads as its
 * locomotion would (the frozen dash velocity still says `move`, exactly as a
 * frozen run does — that sibling is pre-existing and deliberately not cured
 * here). Client-side prediction always passes the default: it has no wall
 * clock to bound by, and its own dodge never freezes.
 */
export function animFor(state, dodgeTellLive = true) {
    if (dodgeTellLive && state.dodgeRemainingMs > 0)
        return 'dodge';
    if (Math.hypot(state.vx, state.vz) > 0.1)
        return 'move';
    return 'idle';
}
/** True while the entity has dodge evasion frames (networking.md "Combat Resolution"). */
export function isEvading(state) {
    return state.dodgeRemainingMs > 0;
}
//# sourceMappingURL=movement.js.map